import { Sparkles, Clock, Flame, Users } from "lucide-react";
import { lerp, easeOut } from "./useFrame";

type Props = { frame: number };

/**
 * 4–11s: A phone-shaped frame appears with a "Cleaning…" shimmer, then the
 * clean recipe card materialises section by section — title → tags → meta →
 * ingredients → method. This is the hero "wow" moment.
 *
 * Local frame range: 240..660 (inside the parent's 0..1200).
 */
export const SceneMagic = ({ frame }: Props) => {
  const f = frame - 240; // 0..420 local
  const frameOpacity = easeOut(lerp(f, [0, 30], [0, 1])) * lerp(f, [400, 420], [1, 1]);
  const cleaning = f < 80;
  const reveal = (start: number) => easeOut(lerp(f, [start, start + 22], [0, 1]));
  const exitAll = easeOut(lerp(f, [400, 420], [0, 1]));

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div
        className="relative w-[78%] max-w-[420px] overflow-hidden rounded-[24px] border border-border bg-card shadow-lift"
        style={{
          opacity: frameOpacity * (1 - exitAll),
          transform: `translateY(${lerp(f, [0, 30], [16, 0])}px) scale(${1 - exitAll * 0.05})`,
          aspectRatio: "9 / 13",
        }}
      >
        {/* Cleaning shimmer overlay */}
        {cleaning && (
          <div className="absolute inset-0 flex items-center justify-center bg-card">
            <div
              className="absolute inset-x-0 h-12 bg-gradient-to-r from-transparent via-primary/15 to-transparent"
              style={{
                top: `${lerp(f, [0, 80], [-10, 110])}%`,
              }}
            />
            <div className="z-10 flex flex-col items-center gap-3 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles
                  className="h-6 w-6"
                  style={{ transform: `rotate(${f * 4}deg)` }}
                />
              </span>
              <p className="font-display text-sm font-semibold text-foreground">
                Cleaning your recipe…
              </p>
            </div>
          </div>
        )}

        {/* Clean recipe materialises */}
        <div className="absolute inset-0 p-5">
          {/* Title */}
          <h3
            className="font-display text-lg font-semibold leading-tight text-foreground"
            style={{
              opacity: reveal(80),
              transform: `translateY(${lerp(f, [80, 102], [6, 0])}px)`,
            }}
          >
            One-pan lemon chicken
          </h3>

          {/* Tags */}
          <div
            className="mt-2 flex flex-wrap gap-1.5"
            style={{ opacity: reveal(110) }}
          >
            {["Dinner", "One-pan", "Quick"].map((t) => (
              <span
                key={t}
                className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground"
              >
                {t}
              </span>
            ))}
          </div>

          {/* Meta */}
          <div
            className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground"
            style={{ opacity: reveal(140) }}
          >
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> 10m
            </span>
            <span className="inline-flex items-center gap-1">
              <Flame className="h-3 w-3" /> 25m
            </span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" /> 4
            </span>
          </div>

          {/* Ingredients */}
          <div
            className="mt-3"
            style={{ opacity: reveal(170) }}
          >
            <p className="font-display text-[11px] font-semibold text-foreground">
              Ingredients
            </p>
            <ul className="mt-1 space-y-0.5 text-[11px] text-foreground">
              {[
                "4 chicken thighs",
                "1 lemon, sliced",
                "3 cloves garlic",
                "1 tsp oregano",
                "200 ml stock",
              ].map((line, i) => (
                <li
                  key={i}
                  style={{
                    opacity: reveal(180 + i * 12),
                    transform: `translateX(${lerp(f, [180 + i * 12, 200 + i * 12], [-6, 0])}px)`,
                  }}
                >
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {/* Method */}
          <div
            className="mt-3"
            style={{ opacity: reveal(260) }}
          >
            <p className="font-display text-[11px] font-semibold text-foreground">
              Method
            </p>
            <ol className="mt-1 space-y-1 text-[11px] text-foreground">
              {[
                "Heat oven to 200°C.",
                "Season chicken & sear.",
                "Add garlic, lemon, stock.",
                "Roast for 20 minutes.",
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex gap-1.5"
                  style={{
                    opacity: reveal(270 + i * 14),
                    transform: `translateY(${lerp(f, [270 + i * 14, 290 + i * 14], [4, 0])}px)`,
                  }}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Caption */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center"
        style={{
          opacity: reveal(330) * (1 - exitAll),
          transform: `translate(-50%, ${lerp(f, [330, 352], [8, 0])}px)`,
        }}
      >
        <p className="font-display text-base font-semibold text-foreground sm:text-lg">
          Cleaned in seconds.
        </p>
      </div>
    </div>
  );
};
